/* eslint-disable @opendoor/no-html-elements -- Atoms has no semantic table primitive. */
import { useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Input } from '@opendoor/atoms/input';
import { Popover } from '@opendoor/atoms/popover';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import {
  IconArrowLeft,
  IconArrowRight,
  IconChevronRight,
  IconDots,
  IconSearch,
} from '@tabler/icons-react';
import { getCustomerNavigationHref } from 'components/account/impersonation';
import { withDashboardOriginSurface } from 'helpers/dashboardSession';

import type { HubSectionId } from './hubSectionOrdering';
import { TransactionProgress } from './TransactionCard';
import type { TransactionCardModel } from './TransactionCard';
import { useHubSectionTracking } from './useHubSectionTracking';

export interface HomePortfolioRow {
  transaction: TransactionCardModel;
  street: string;
  locality: string;
  isActive: boolean;
  clients?: string[];
  keyDate?: string | null;
  detailsUnavailable?: boolean;
  visibility?: { isHidden: boolean; canHide: boolean };
}

interface PortfolioPageControls {
  activeTab: boolean;
  search: string;
  page: number;
  counts?: { total: number; active: number; other: number };
  hasNextPage: boolean;
  onTabChange: (active: boolean) => void;
  onSearchChange: (search: string) => void;
  onPrevious: () => void;
  onNext: () => void;
}

interface HomePortfolioTableProps {
  section: HubSectionId;
  title: string;
  rows: HomePortfolioRow[];
  action: { label: string; href: string };
  isLoading: boolean;
  isRefreshing?: boolean;
  hasError: boolean;
  onRetry: () => void;
  pageControls?: PortfolioPageControls;
  showClientColumn?: boolean;
  showKeyDateColumn?: boolean;
  searchScope?: 'address' | 'address-or-client';
  searchCoverage?: 'portfolio' | 'available';
  searchMaxLength?: number;
  hasPartialError?: boolean;
  onVisibilityChange?: (row: HomePortfolioRow, isHidden: boolean) => void;
  isUpdatingVisibility?: boolean;
}

const PAGE_SIZE = 5;
const ADDRESS_COLUMN_CLASS =
  'sticky left-0 z-10 w-42 min-w-42 bg-od-bg-primary md:w-50 md:min-w-50';
const PAGER_BUTTON_CLASS =
  'size-8 rounded-lg p-0 disabled:not-aria-busy:border-od-border-secondary disabled:not-aria-busy:bg-transparent disabled:not-aria-busy:!text-od-content-inactive-primary';

const portfolioTableClass = (showClientColumn: boolean, showKeyDateColumn: boolean) =>
  cn(
    'table w-full border-collapse text-left',
    showClientColumn && showKeyDateColumn && 'min-w-165',
    showClientColumn && !showKeyDateColumn && 'min-w-137',
    !showClientColumn && showKeyDateColumn && 'min-w-135',
    !showClientColumn && !showKeyDateColumn && 'min-w-107',
  );

export function HomePortfolioTable({
  section,
  title,
  rows,
  action,
  isLoading,
  isRefreshing = false,
  hasError,
  onRetry,
  pageControls,
  showClientColumn = false,
  showKeyDateColumn = false,
  searchScope = 'address',
  searchCoverage = 'available',
  searchMaxLength,
  hasPartialError = false,
  onVisibilityChange,
  isUpdatingVisibility = false,
}: HomePortfolioTableProps) {
  const [localActiveTab, setActiveTab] = useState(true);
  const [localSearch, setSearch] = useState('');
  const [selectedPage, setSelectedPage] = useState(0);
  const trackInteraction = useHubSectionTracking(section);
  const activeTab = pageControls?.activeTab ?? localActiveTab;
  const search = pageControls?.search ?? localSearch;
  const query = search.trim().toLocaleLowerCase();
  const matches = rows.filter(({ street, locality }) =>
    `${street} ${locality}`.toLocaleLowerCase().includes(query),
  );
  const filtered = pageControls ? rows : matches.filter(({ isActive }) => isActive === activeTab);
  const page =
    pageControls?.page ??
    Math.min(selectedPage, Math.max(0, Math.ceil(filtered.length / PAGE_SIZE) - 1));
  const start = page * PAGE_SIZE;
  const visible = pageControls ? rows : filtered.slice(start, start + PAGE_SIZE);
  const unavailable = isLoading || hasError;

  const columns = [
    'Address',
    ...(showClientColumn ? ['Client'] : []),
    'Stage',
    ...(showKeyDateColumn ? ['Key date'] : []),
    '',
  ];
  const renderHeaders = () => (
    <thead className="table-header-group bg-od-bg-secondary">
      <tr className="table-row border-b border-od-border-secondary">
        {columns.map((label) => (
          <th
            key={label}
            scope="col"
            className={cn(
              'table-cell px-2 py-3 first:pl-4',
              label === 'Address' && cn(ADDRESS_COLUMN_CLASS, 'bg-od-bg-secondary'),
              label === 'Stage' && 'min-w-45',
            )}
          >
            <Text
              as="span"
              className={cn('text-body-sm text-od-content-tertiary', !label && 'sr-only')}
            >
              {label || 'Open home'}
            </Text>
          </th>
        ))}
      </tr>
    </thead>
  );
  const renderResults = () => {
    if (isLoading)
      return (
        <Box role="status" className="relative isolate block overflow-x-auto">
          <Text className="sr-only">Loading homes…</Text>
          <table aria-hidden className={portfolioTableClass(showClientColumn, showKeyDateColumn)}>
            {renderHeaders()}
            <tbody>
              {Array.from({ length: PAGE_SIZE }, (_, index) => (
                <tr key={index} className="h-[63px] border-b border-od-border-secondary">
                  {columns.map((label) => (
                    <td
                      key={label}
                      className={cn(
                        'px-2 py-3 first:pl-4',
                        label === 'Address' && ADDRESS_COLUMN_CLASS,
                        label === 'Stage' && 'min-w-45',
                      )}
                    >
                      <Col className="gap-2">
                        <Box className="skeleton h-3 w-16 rounded-md bg-od-bg-tertiary" />
                        {label === 'Address' && (
                          <Box className="skeleton h-3 w-10 rounded-md bg-od-bg-tertiary" />
                        )}
                      </Col>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
    if (hasError)
      return (
        <Col role="alert" className="items-center gap-2 px-4 py-16 text-center">
          <Text className="text-label-sm text-od-content-primary">
            We couldn’t load your homes.
          </Text>
          <Text className="text-body-sm text-od-content-secondary">
            Check your connection and try again.
          </Text>
          <Button
            aria-label={`Retry ${title.toLowerCase()}`}
            analyticsName={`${section}-portfolio-retry`}
            variant="secondary"
            size="md"
            onClick={onRetry}
          >
            Try again
          </Button>
        </Col>
      );
    if (visible.length === 0) {
      const noSearchMatches = pageControls?.counts
        ? pageControls.counts.active + pageControls.counts.other === 0
        : matches.length === 0;
      let emptyMessage = activeTab ? 'No active homes' : 'No homes in other';
      let description: string | undefined = activeTab
        ? 'Past offers are under "Other"'
        : 'Closed, expired, and hidden homes will show up here.';
      if (query) {
        emptyMessage = noSearchMatches
          ? `No homes match “${search.trim()}”`
          : `No ${activeTab ? 'active' : 'other'} homes match “${search.trim()}”`;
        description =
          searchCoverage === 'portfolio'
            ? 'Search covers every address and client in Active and Other.'
            : undefined;
      }
      return (
        <Col role="status" className="items-center gap-2 px-4 py-16 text-center">
          <Text className="text-label-sm text-od-content-primary">{emptyMessage}</Text>
          {description && (
            <Text className="text-body-sm text-od-content-secondary">{description}</Text>
          )}
        </Col>
      );
    }
    return (
      <Box
        className="relative isolate block overflow-x-auto focus-visible:outline-1 focus-visible:outline-solid focus-visible:-outline-offset-2 focus-visible:outline-od-border-focused-secondary"
        role="region"
        aria-label={`${title} results`}
        tabIndex={0}
        aria-busy={isRefreshing}
      >
        <table
          className={portfolioTableClass(showClientColumn, showKeyDateColumn)}
          aria-label={title}
        >
          {renderHeaders()}
          <tbody className="table-row-group">
            {visible.map((row) => {
              const {
                transaction,
                street,
                locality,
                clients,
                keyDate,
                detailsUnavailable,
                visibility,
              } = row;
              const href = getCustomerNavigationHref(withDashboardOriginSurface(transaction.href));
              return (
                <tr
                  key={transaction.id}
                  className={cn(
                    'relative table-row h-[63px] border-b border-od-border-secondary hover:bg-od-bg-hover-primary',
                    transaction.isMuted && 'text-od-content-secondary',
                  )}
                >
                  <td className={cn('table-cell py-3 pr-2 pl-4', ADDRESS_COLUMN_CLASS)}>
                    <Box
                      href={href}
                      aria-label={`View ${street}`}
                      title={`${street}, ${locality}`}
                      className="flex-col"
                      onClick={trackInteraction}
                    >
                      <Text
                        as="span"
                        className={cn(
                          'truncate text-label-sm',
                          visibility?.isHidden && 'text-od-content-tertiary',
                        )}
                      >
                        {street}
                      </Text>
                      <Text as="span" className="truncate text-body-sm text-od-content-secondary">
                        {locality}
                        {visibility?.isHidden ? ' · Hidden' : ''}
                      </Text>
                    </Box>
                  </td>
                  {showClientColumn && (
                    <td className="table-cell px-2 py-3">
                      <Col className="gap-1">
                        {(clients?.length ? clients : ['—']).map((name) => (
                          <Text
                            key={name}
                            as="span"
                            className="text-body-sm text-od-content-secondary"
                          >
                            {name}
                          </Text>
                        ))}
                      </Col>
                    </td>
                  )}
                  <td className="table-cell min-w-45 px-2 py-3">
                    <Row className="items-center gap-2">
                      {!detailsUnavailable && (
                        <TransactionProgress
                          total={transaction.totalSegments}
                          completed={transaction.completedSegments}
                          isMuted={transaction.isMuted}
                          isComplete={transaction.isComplete}
                        />
                      )}
                      <Text as="span" className="text-body-sm text-od-content-secondary">
                        {transaction.statusLabel}
                      </Text>
                    </Row>
                  </td>
                  {showKeyDateColumn && (
                    <td className="table-cell px-2 py-3">
                      <Text
                        as="span"
                        className="whitespace-nowrap text-body-sm text-od-content-secondary"
                      >
                        {keyDate ?? '—'}
                      </Text>
                    </td>
                  )}
                  <td className="table-cell px-2 py-2 sm:py-3">
                    <Row className="items-center justify-end gap-1">
                      {onVisibilityChange &&
                        visibility &&
                        (visibility.isHidden || visibility.canHide) && (
                          // The row link covers the row, so the menu stays above it.
                          <Box className="relative z-20">
                            <PortfolioVisibilityMenu
                              row={row}
                              disabled={isUpdatingVisibility || isRefreshing}
                              onChange={onVisibilityChange}
                            />
                          </Box>
                        )}
                      {/* Sticky cells contain absolute children, so the row overlay lives here. */}
                      <Box
                        href={href}
                        aria-label={`View ${street}`}
                        aria-hidden
                        tabIndex={-1}
                        className="after:absolute after:inset-0 after:z-10 after:content-['']"
                        onClick={trackInteraction}
                      >
                        <IconChevronRight
                          aria-hidden
                          size={16}
                          className="text-od-content-tertiary"
                        />
                      </Box>
                    </Row>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    );
  };

  return (
    <Col className="w-full gap-4">
      <Row className="flex-wrap items-center justify-between gap-4">
        <Text as="h2" className="text-subheader-sm text-od-content-primary">
          {title}
        </Text>
        <Button
          href={getCustomerNavigationHref(action.href)}
          analyticsName={`${section}-portfolio-create`}
          aria-label={action.label}
          size="md"
          variant={section === 'home-sale' ? 'primary' : 'secondary'}
          className="w-full sm:w-auto"
        >
          {action.label}
        </Button>
      </Row>
      <Col className="overflow-hidden rounded-xl border border-od-border-primary">
        <Row
          className="gap-2 border-b border-od-border-secondary px-4"
          aria-label={`${title} filters`}
        >
          {[
            { active: true, label: 'Active' },
            { active: false, label: 'Other' },
          ].map(({ active, label }) => (
            <Button
              key={label}
              aria-label={`${title}: ${label}`}
              aria-pressed={activeTab === active}
              analyticsName={`${section}-portfolio-${label.toLowerCase()}`}
              size="md"
              variant="secondary"
              disabled={isUpdatingVisibility || (unavailable && !pageControls)}
              className={cn(
                'border-0 rounded-none focus-visible:-outline-offset-2',
                activeTab === active && 'border-b-2 border-od-border-selected',
              )}
              onClick={() => {
                if (pageControls) pageControls.onTabChange(active);
                else setActiveTab(active);
                setSelectedPage(0);
                trackInteraction();
              }}
            >
              {label}
              {pageControls?.counts && (
                <Text as="span" className="text-body-sm text-od-content-tertiary">
                  {active ? pageControls.counts.active : pageControls.counts.other}
                </Text>
              )}
            </Button>
          ))}
        </Row>
        <Box className="border-b border-od-border-secondary px-4 py-3">
          <Box className="relative block w-full">
            <IconSearch
              aria-hidden
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-od-content-tertiary"
            />
            <Input
              type="search"
              size="xs"
              className="w-full pl-9"
              aria-label={`Search ${title.toLowerCase()} ${searchScope === 'address-or-client' ? 'addresses or clients' : 'addresses'}`}
              placeholder={
                searchScope === 'address-or-client' ? 'Search address or client' : 'Search address'
              }
              value={search}
              disabled={isUpdatingVisibility || (unavailable && !pageControls)}
              maxLength={searchMaxLength}
              onValueChange={(value) => {
                const text = typeof value === 'string' ? value : '';
                if (pageControls) pageControls.onSearchChange(text);
                else setSearch(text);
                setSelectedPage(0);
              }}
            />
          </Box>
        </Box>
        {hasPartialError && !unavailable && (
          <Row role="alert" className="flex-wrap items-center gap-3 px-4 py-3">
            <Text className="text-body-sm text-od-content-secondary">
              Some home details couldn’t load.
            </Text>
            <Button
              aria-label={`Retry ${title.toLowerCase()} details`}
              analyticsName={`${section}-portfolio-retry-details`}
              variant="secondary"
              size="md"
              onClick={onRetry}
              disabled={isRefreshing}
            >
              Try again
            </Button>
          </Row>
        )}
        {isRefreshing && !unavailable && (
          <Text role="status" className="px-4 py-3 text-body-sm text-od-content-secondary">
            Loading homes…
          </Text>
        )}
        {renderResults()}
        {!unavailable && (filtered.length > 0 || (pageControls?.page ?? 0) > 0) && (
          <Row className="items-center justify-between gap-4 px-4 py-3">
            {pageControls?.counts && visible.length > 0 && (
              <Text as="span" className="text-body-sm text-od-content-secondary">
                {start + 1}–{start + visible.length} of {pageControls.counts.total}
              </Text>
            )}
            <Row className="flex-1 justify-end gap-2">
              <Button
                aria-label={`Previous ${title.toLowerCase()} page`}
                analyticsName={`${section}-portfolio-previous`}
                variant="secondary"
                size="sm"
                className={PAGER_BUTTON_CLASS}
                disabled={isUpdatingVisibility || isRefreshing || page === 0}
                onClick={() =>
                  pageControls ? pageControls.onPrevious() : setSelectedPage(page - 1)
                }
              >
                <IconArrowLeft aria-hidden size={16} />
              </Button>
              <Button
                aria-label={`Next ${title.toLowerCase()} page`}
                analyticsName={`${section}-portfolio-next`}
                variant="secondary"
                size="sm"
                className={PAGER_BUTTON_CLASS}
                disabled={
                  isUpdatingVisibility ||
                  isRefreshing ||
                  (pageControls ? !pageControls.hasNextPage : start + PAGE_SIZE >= filtered.length)
                }
                onClick={() => (pageControls ? pageControls.onNext() : setSelectedPage(page + 1))}
              >
                <IconArrowRight aria-hidden size={16} />
              </Button>
            </Row>
          </Row>
        )}
      </Col>
    </Col>
  );
}

function PortfolioVisibilityMenu({
  row,
  disabled,
  onChange,
}: {
  row: HomePortfolioRow;
  disabled: boolean;
  onChange: (row: HomePortfolioRow, isHidden: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const isHidden = row.visibility?.isHidden ?? false;
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        render={(props) => (
          <Button
            {...props}
            aria-label={`Actions for ${row.street}`}
            analyticsName="home-purchase-portfolio-actions"
            variant="secondary"
            size="sm"
            disabled={disabled}
            className="h-11 w-11 border-0 p-0 sm:h-8 sm:w-8"
          >
            <IconDots aria-hidden size={16} />
          </Button>
        )}
      />
      <Popover align="end" size="sm" className="w-52 border border-od-border-primary p-1 ring-0">
        <Popover.Title className="sr-only">Actions for {row.street}</Popover.Title>
        <Button
          variant="secondary"
          aria-label={isHidden ? 'Unhide' : 'Hide offer'}
          className="w-full justify-start border-0"
          disabled={disabled}
          analyticsName={
            isHidden ? 'home-purchase-portfolio-unhide' : 'home-purchase-portfolio-hide'
          }
          onClick={() => {
            setOpen(false);
            onChange(row, !isHidden);
          }}
        >
          {isHidden ? 'Unhide' : 'Hide offer'}
        </Button>
      </Popover>
    </Popover.Root>
  );
}
